// ScrollImage.jsx
import React, { useEffect, useRef, useMemo } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './ScrollImage.css';

gsap.registerPlugin(ScrollTrigger);

const ScrollImage = () => {
    const sectionRef = useRef(null);
    const row1Ref = useRef(null);
    const row2Ref = useRef(null);

    // ── Local Images from public/images/home/ ────────────────
    const localImages = useMemo(() => [
        "/images/home/scroll1 (1).webp",
        "/images/home/scroll1 (2).webp",
        "/images/home/scroll1 (3).webp"
    ], []);

    // UPPER: 1, 2, 3 order
    const row1Images = localImages;
    
    // DOWN: 3, 1, 2 order as requested
    const row2Images = useMemo(() => {
        if (localImages.length < 2) return localImages;
        const rearranged = [...localImages];
        const last = rearranged.pop();
        rearranged.unshift(last);
        return rearranged;
    }, [localImages]);

    useEffect(() => {
        let timer;
        const ctx = gsap.context(() => {
            console.log('ScrollImage: Initializing ScrollTrigger');

            // ── Row 1 → slides LEFT by exactly one set (33.33%) ──
            gsap.fromTo(
                row1Ref.current,
                { xPercent: 0 },
                {
                    xPercent: -33.3333,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: sectionRef.current,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: 1, // Tighter connection to scroll, feel more perfect to mouse
                        invalidateOnRefresh: true,
                    },
                }
            );

            // ── Row 2 → slides RIGHT by exactly one set (33.33%) ──
            gsap.fromTo(
                row2Ref.current,
                { xPercent: -33.3333 },
                {
                    xPercent: 0,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: sectionRef.current,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: 1,
                        invalidateOnRefresh: true,
                    },
                }
            );

            // Refresh ScrollTrigger after a short delay and when images are likely loaded
            timer = setTimeout(() => {
                ScrollTrigger.refresh();
            }, 500);

        }, sectionRef);

        // Explicitly refresh all triggers when window resizes
        const handleResize = () => {
            clearTimeout(timer);
            timer = setTimeout(() => ScrollTrigger.refresh(), 200);
        };
        window.addEventListener('resize', handleResize);

        return () => {
            ctx.revert();
            clearTimeout(timer);
            window.removeEventListener('resize', handleResize);
        };
    }, []); 

    // ── Reusable card ──────────────────────────────────────
    const ImageCard = ({ src, alt }) => (
        <div className="scroll-item relative flex-shrink-0
                        w-[280px] sm:w-[420px] md:w-[560px] lg:w-[500px]
                        aspect-[16/9] rounded-[2rem] overflow-hidden
                        bg-white shadow-xl">

            <img
                src={src}
                alt={alt}
                loading="lazy"
                className="w-full h-full object-cover"
            />
        </div>
    );

    return (
        <section
            ref={sectionRef}
            className="scroll-image-section relative overflow-hidden py-16"
        >
            {/* background and heading removed — now matches WhyChooseUs visual style */}

            {/* ── Rows ── */}
            <div className="relative z-10 flex flex-col gap-10 md:gap-16">
                
                {/* ROW 1 — starts at x:0, moves left */}
                <div
                    ref={row1Ref}
                    className="flex gap-6 md:gap-10 whitespace-nowrap will-change-transform"
                    style={{ width: 'max-content' }}
                >
                    {[...row1Images, ...row1Images, ...row1Images].map((src, i) => (
                        <ImageCard key={i} src={src} alt={`Project ${i + 1}`} />
                    ))}
                </div>

                {/* ROW 2 — starts shifted left, moves back right */}
                <div
                    ref={row2Ref}
                    className="flex gap-6 md:gap-10 whitespace-nowrap will-change-transform"
                    style={{ width: 'max-content', marginLeft: '-15%' }}
                >
                    {[...row2Images, ...row2Images, ...row2Images].map((src, i) => (
                        <ImageCard key={i} src={src} alt={`Project ${i + 1}`} />
                    ))}
                </div>
            </div>

            {/* ── Ambient blobs ── */}
            <div className="blob blob-blue"   aria-hidden />
            <div className="blob blob-teal" aria-hidden />
        </section>
    );
};

export default ScrollImage;